import { argon2id } from 'hash-wasm';
import { derivePepper } from './pinPepperClient.helpers';

const SALT_BYTES = 16;
const IV_BYTES = 12;
const ARGON2_PARAMS = { parallelism: 1, iterations: 3, memorySize: 32 * 1024, hashLength: 32 } as const;
const HKDF_INFO = new TextEncoder().encode('savi-connect-pin-wrap-v1');

export class PinDecryptError extends Error {
  constructor() {
    super('Incorrect PIN');
    this.name = 'PinDecryptError';
  }
}

export const toBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  bytes.forEach(byte => (binary += String.fromCharCode(byte)));
  return btoa(binary);
};

export const fromBase64 = (b64: string): Uint8Array => Uint8Array.from(atob(b64), c => c.charCodeAt(0));

/** Contents of a pin.key object. `rtExpiresAt` is plain (unencrypted) metadata, epoch ms. */
export type PinKeyPayload = {
  v: 1;
  salt: string;
  iv: string;
  ciphertext: string;
  rtExpiresAt: number;
};

export const encodePinKey = (payload: PinKeyPayload): string => JSON.stringify(payload);

export const decodePinKey = (raw: string): PinKeyPayload => {
  const payload = JSON.parse(raw) as Partial<PinKeyPayload>;
  if (payload.v !== 1 || !payload.salt || !payload.iv || !payload.ciphertext) {
    throw new Error('Unsupported pin.key format');
  }
  return payload as PinKeyPayload;
};

export const generateSalt = (): Uint8Array => crypto.getRandomValues(new Uint8Array(SALT_BYTES));

/** Zeroes key material in place; non-extractable CryptoKeys can only be dereferenced by the caller. */
export const wipe = (...buffers: (Uint8Array | undefined | null)[]): void => {
  buffers.forEach(buffer => buffer?.fill(0));
};

/** Client-side KDF: the Argon2id output is what gets blinded and sent to the pepper signer. */
export const deriveKey = (pin: string, salt: Uint8Array): Promise<Uint8Array> =>
  argon2id({ password: pin, salt, ...ARGON2_PARAMS, outputType: 'binary' });

const deriveWrapKey = async (kClient: Uint8Array, pepper: Uint8Array): Promise<CryptoKey> => {
  const keyMaterial = await crypto.subtle.importKey('raw', kClient, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: pepper, info: HKDF_INFO },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
};

/**
 * Opaque identifier the pepper signer rate-limits on. Emails are only unique within an org, so the
 * pair is hashed: the signer (unauthenticated, logged) never sees an email, and ":" can't appear in
 * one, so distinct (email, org) pairs can't collide.
 */
export const derivePinIdentity = async (email: string, orgId: string): Promise<string> => {
  const input = new TextEncoder().encode(`${email.trim().toLowerCase()}:${orgId}`);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', input));
  return toBase64(digest).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * K_wrap = HKDF(Argon2id(PIN, salt), pepper). The VOPRF blind -> evaluate -> unblind round trip is
 * done by derivePepper; the blinded value is the only thing that leaves the browser.
 */
export const deriveWrapKeyFromPin = async (pinIdentity: string, pin: string, salt: Uint8Array): Promise<CryptoKey> => {
  const kClient = await deriveKey(pin, salt);
  let pepper: Uint8Array | undefined;
  try {
    pepper = await derivePepper(pinIdentity, kClient);
    return await deriveWrapKey(kClient, pepper);
  } finally {
    wipe(kClient, pepper);
  }
};

export const wrap = async (key: CryptoKey, plaintext: string): Promise<{ iv: string; ciphertext: string }> => {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plaintext));
  return { iv: toBase64(iv), ciphertext: toBase64(new Uint8Array(encrypted)) };
};

/** Throws PinDecryptError when the key is wrong (AES-GCM authentication failure). */
export const unwrap = async (key: CryptoKey, iv: string, ciphertext: string): Promise<string> => {
  try {
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64(iv) }, key, fromBase64(ciphertext));
    return new TextDecoder().decode(decrypted);
  } catch {
    throw new PinDecryptError();
  }
};
